export const TRATTENUTA_RIMBORSO_STANDARD = 9.9;

export function leggiNumero(value) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function arrotondaImporto(value) {
  return Math.round((leggiNumero(value) + Number.EPSILON) * 100) / 100;
}

export function leggiQuantitaArticolo(articolo) {
  const parsed = parseInt(articolo?.["Quantità"], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function leggiImportoUnitario(articolo) {
  return arrotondaImporto(articolo?.["Importo unitario"]);
}

export function calcolaImportoArticolo(articolo, quantita) {
  const maxQuantita = leggiQuantitaArticolo(articolo);
  const quantitaPulita = Math.max(
    0,
    Math.min(maxQuantita, parseInt(quantita, 10) || 0),
  );
  return arrotondaImporto(leggiImportoUnitario(articolo) * quantitaPulita);
}

export function formattaEuro(value) {
  return arrotondaImporto(value).toFixed(2).replace(".", ",");
}

export function creaRigaConfigurazione(articolo) {
  return {
    selected: false,
    quantity: 0,
    amount: 0,
    maxQuantity: leggiQuantitaArticolo(articolo),
  };
}

function leggiConfigurazionePersistita(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}

function trovaRigaPersistita(righe, articolo, index, indiciUsati) {
  const idArticolo = articolo?.["ID Articolo"];
  const codiceArticolo = articolo?.["Cod Viglietti Sport"];
  let indice = -1;

  if (idArticolo !== null && idArticolo !== undefined) {
    indice = righe.findIndex(
      (riga, rigaIndex) =>
        !indiciUsati.has(rigaIndex) &&
        riga?.articleId !== null &&
        riga?.articleId !== undefined &&
        String(riga.articleId) === String(idArticolo),
    );
  }
  if (indice < 0 && codiceArticolo) {
    indice = righe.findIndex(
      (riga, rigaIndex) =>
        !indiciUsati.has(rigaIndex) &&
        riga?.articleCode &&
        String(riga.articleCode) === String(codiceArticolo),
    );
  }
  if (indice < 0 && righe[index] && !indiciUsati.has(index)) {
    indice = index;
  }
  if (indice >= 0) {
    indiciUsati.add(indice);
    return righe[indice];
  }
  return null;
}

function ricostruisciRigheDaTotale(reso, articoliSpedizione) {
  let residuoMerce = arrotondaImporto(reso?.["resiValore_merce"]);

  return (articoliSpedizione || []).map((articolo) => {
    const riga = creaRigaConfigurazione(articolo);
    if (residuoMerce <= 0) {
      return riga;
    }
    const importoPieno = calcolaImportoArticolo(articolo, riga.maxQuantity);
    if (importoPieno <= 0) {
      return riga;
    }
    if (residuoMerce >= importoPieno - 0.01) {
      residuoMerce = arrotondaImporto(residuoMerce - importoPieno);
      return {
        ...riga,
        selected: true,
        quantity: riga.maxQuantity,
        amount: importoPieno,
      };
    }
    const importoUnitario = leggiImportoUnitario(articolo);
    const quantity =
      importoUnitario > 0
        ? Math.min(
            riga.maxQuantity,
            Math.max(1, Math.round(residuoMerce / importoUnitario)),
          )
        : 1;
    const amount = arrotondaImporto(Math.min(importoPieno, residuoMerce));
    residuoMerce = 0;
    return { ...riga, selected: true, quantity, amount };
  });
}

export function creaConfigurazioneIniziale(
  reso,
  articoliSpedizione,
  importoSpedizione,
  importoContrassegno,
) {
  const persistita = leggiConfigurazionePersistita(
    reso?.resiConfigurazione_rimborso,
  );
  let articoli;

  if (persistita && Array.isArray(persistita.articoli)) {
    const indiciUsati = new Set();
    articoli = (articoliSpedizione || []).map((articolo, index) => {
      const base = creaRigaConfigurazione(articolo);
      const salvata = trovaRigaPersistita(
        persistita.articoli,
        articolo,
        index,
        indiciUsati,
      );
      if (!salvata) {
        return base;
      }
      const quantity = Math.max(
        0,
        Math.min(base.maxQuantity, parseInt(salvata.quantity, 10) || 0),
      );
      const amount = Math.max(0, arrotondaImporto(salvata.amount));
      return {
        ...base,
        selected: Boolean(salvata.selected) && quantity > 0 && amount > 0,
        quantity,
        amount,
      };
    });
  } else {
    articoli = ricostruisciRigheDaTotale(reso, articoliSpedizione);
  }

  const spedizioneRitorno = leggiNumero(reso?.["resiSpedizione_R"]);
  const spedizioneAndata = leggiNumero(reso?.["resiSpedizione_A"]);
  return {
    totaleSpedizione: persistita
      ? Boolean(persistita.totaleSpedizione)
      : spedizioneAndata > 0,
    detrai990: persistita ? Boolean(persistita.detrai990) : spedizioneRitorno < 0,
    detrai990Importo: persistita
      ? Math.max(0, arrotondaImporto(persistita.detrai990Importo))
      : spedizioneRitorno < 0
        ? Math.abs(spedizioneRitorno)
        : TRATTENUTA_RIMBORSO_STANDARD,
    detraiSpedizione: persistita
      ? Boolean(persistita.detraiSpedizione)
      : spedizioneAndata < 0,
    detraiSpedizioneImporto: persistita
      ? Math.max(0, arrotondaImporto(persistita.detraiSpedizioneImporto))
      : spedizioneAndata < 0
        ? Math.abs(spedizioneAndata)
        : arrotondaImporto(importoSpedizione),
    detraiContrassegno: persistita
      ? Boolean(persistita.detraiContrassegno)
      : false,
    detraiContrassegnoImporto: persistita
      ? Math.max(0, arrotondaImporto(persistita.detraiContrassegnoImporto))
      : arrotondaImporto(importoContrassegno),
    articoli,
  };
}

export function creaConfigurazioneInteraSpedizione(
  configurazione,
  articoliSpedizione,
  importoContrassegno,
) {
  const contrassegnoDaDb = arrotondaImporto(importoContrassegno);
  return {
    ...configurazione,
    totaleSpedizione: true,
    detraiContrassegno:
      contrassegnoDaDb > 0 ? true : configurazione.detraiContrassegno,
    detraiContrassegnoImporto:
      contrassegnoDaDb > 0
        ? contrassegnoDaDb
        : configurazione.detraiContrassegnoImporto,
    articoli: (articoliSpedizione || []).map((articolo) => ({
      selected: true,
      quantity: leggiQuantitaArticolo(articolo),
      amount: calcolaImportoArticolo(
        articolo,
        leggiQuantitaArticolo(articolo),
      ),
      maxQuantity: leggiQuantitaArticolo(articolo),
    })),
  };
}

export function preparaConfigurazionePerPersistenza(
  configurazione,
  articoliSpedizione,
) {
  return {
    totaleSpedizione: Boolean(configurazione?.totaleSpedizione),
    detrai990: Boolean(configurazione?.detrai990),
    detrai990Importo: Math.max(
      0,
      arrotondaImporto(configurazione?.detrai990Importo),
    ),
    detraiSpedizione: Boolean(configurazione?.detraiSpedizione),
    detraiSpedizioneImporto: Math.max(
      0,
      arrotondaImporto(configurazione?.detraiSpedizioneImporto),
    ),
    detraiContrassegno: Boolean(configurazione?.detraiContrassegno),
    detraiContrassegnoImporto: Math.max(
      0,
      arrotondaImporto(configurazione?.detraiContrassegnoImporto),
    ),
    articoli: (articoliSpedizione || []).map((articolo, index) => {
      const riga = configurazione?.articoli?.[index] ||
        creaRigaConfigurazione(articolo);
      return {
        articleId: articolo?.["ID Articolo"] ?? null,
        articleCode: articolo?.["Cod Viglietti Sport"] ?? null,
        selected: Boolean(riga.selected),
        quantity: Math.max(0, parseInt(riga.quantity, 10) || 0),
        amount: Math.max(0, arrotondaImporto(riga.amount)),
      };
    }),
  };
}

export function calcolaComposizioneRimborso(
  configurazione,
  importoSpedizione,
  importoContrassegno,
) {
  const importoMerce = arrotondaImporto(
    (configurazione?.articoli || []).reduce(
      (acc, articolo) =>
        articolo?.selected ? acc + leggiNumero(articolo.amount) : acc,
      0,
    ),
  );
  let importoSpedizioneAndata = configurazione?.totaleSpedizione
    ? leggiNumero(importoSpedizione) + leggiNumero(importoContrassegno)
    : 0;
  if (configurazione?.detraiSpedizione) {
    importoSpedizioneAndata -= leggiNumero(
      configurazione.detraiSpedizioneImporto,
    );
  }
  importoSpedizioneAndata = arrotondaImporto(importoSpedizioneAndata);
  const totaleDetrazioniRitorno = arrotondaImporto(
    (configurazione?.detrai990
      ? leggiNumero(configurazione.detrai990Importo)
      : 0) +
      (configurazione?.detraiContrassegno
        ? leggiNumero(configurazione.detraiContrassegnoImporto)
        : 0),
  );
  const importoSpedizioneRitorno =
    totaleDetrazioniRitorno > 0 ? -totaleDetrazioniRitorno : 0;

  return {
    importoMerce,
    importoSpedizioneAndata,
    importoSpedizioneRitorno,
    totaleRimborso: arrotondaImporto(
      importoMerce + importoSpedizioneAndata + importoSpedizioneRitorno,
    ),
  };
}
